import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import RenewalModal from '../components/renewals/RenewalModal';
import RenewalEditModal from '../components/renewals/RenewalEditModal';
import { Button, Card, Select, Skeleton, Tabs, useToast } from '../../design-system';
import '../styles/Renewals.css';

// Everything the firm's clients hold that expires — licences, registrations,
// LUTs, DSCs — ranked by how much trouble it is in.
//
// The board opens on what is WRONG rather than on a full alphabetical list: a
// renewals module whose first screen is "here are 400 records" has failed at
// the only job it has.

export const STATE = {
  lapsed: { label: 'Lapsed', cls: 'rnw-s-lapsed' },
  due_today: { label: 'Due today', cls: 'rnw-s-today' },
  due_week: { label: 'This week', cls: 'rnw-s-week' },
  due_soon: { label: 'Due soon', cls: 'rnw-s-soon' },
  active: { label: 'Active', cls: 'rnw-s-active' },
  renewed: { label: 'Renewed', cls: 'rnw-s-renewed' },
  cancelled: { label: 'Cancelled', cls: 'rnw-s-cancelled' },
  unknown: { label: 'No date', cls: 'rnw-s-unknown' },
};

export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

// "12 days left" / "lapsed 40 days ago" — the number people actually act on.
export const dueLabel = (days) => {
  if (days === null || days === undefined) return '—';
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'today';
  return `${days}d left`;
};

const TABS = [
  { key: 'board', label: 'Needs attention' },
  { key: 'all', label: 'All renewals' },
  { key: 'clients', label: 'By client' },
];

function OrgAdminRenewals() {
  const toast = useToast();
  const [tab, setTab] = useState('board');
  const [summary, setSummary] = useState(null);
  const [all, setAll] = useState(null);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => setRefreshKey((n) => n + 1);

  const [stateFilter, setStateFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);

  const [renewing, setRenewing] = useState(null);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    apiClient.get('/api/expirables/types')
      .then((r) => setTypes(r.data.types || []))
      .catch(() => setTypes([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'all') {
        const r = await apiClient.get('/api/expirables', {
          params: { deleted: showDeleted ? 'true' : undefined },
        });
        setAll(r.data);
      } else {
        const r = await apiClient.get('/api/expirables/summary');
        setSummary(r.data);
      }
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not load renewals');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, showDeleted, refreshKey]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const base = tab === 'all' ? (all || []) : (summary?.rows || []);
    const q = search.trim().toLowerCase();
    return base.filter((r) => {
      if (stateFilter !== 'all' && r.state !== stateFilter) return false;
      if (typeFilter !== 'all' && r.type !== typeFilter) return false;
      if (!q) return true;
      return (r.clientName || '').toLowerCase().includes(q)
        || (r.number || '').toLowerCase().includes(q)
        || (r.typeLabel || '').toLowerCase().includes(q);
    });
  }, [tab, all, summary, stateFilter, typeFilter, search]);

  const c = summary?.counts;

  const remove = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${row.typeLabel} for ${row.clientName}? Its renewal history is kept.`)) return;
    try {
      await apiClient.delete(`/api/expirables/${row.id}`);
      toast.success('Removed');
      bump();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not remove');
    }
  };

  const restore = async (row) => {
    try {
      await apiClient.patch(`/api/expirables/${row.id}/restore`);
      toast.success('Restored');
      bump();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not restore');
    }
  };

  const table = (list) => (
    <div className="rnw-scroll">
      <table className="rnw-table">
        <thead>
          <tr>
            <th>Client</th><th>What</th><th>Number</th>
            <th>Expires</th><th className="rnw-num">Due</th>
            <th>Status</th><th />
          </tr>
        </thead>
        <tbody>
          {list.map((r) => {
            const s = STATE[r.state] || STATE.unknown;
            return (
              <tr key={r.id} className={r.state === 'lapsed' ? 'rnw-row-bad' : ''}>
                <td>
                  <span className="rnw-name">{r.clientName}</span>
                  {r.clientCode && <span className="rnw-code">{r.clientCode}</span>}
                </td>
                <td>
                  <span className="rnw-type">{r.typeLabel}</span>
                  {r.ownerType === 'Signatory' && <span className="rnw-code">held by a signatory</span>}
                  {r.renewalCount > 0 && (
                    <span className="rnw-code">renewed {r.renewalCount}×</span>
                  )}
                </td>
                <td className="rnw-muted">{r.number || '—'}</td>
                <td className="rnw-nowrap">{fmtDate(r.validTo)}</td>
                <td className={`rnw-num rnw-nowrap${r.daysLeft < 0 ? ' rnw-bad' : ''}`}>
                  <b>{dueLabel(r.daysLeft)}</b>
                </td>
                <td>
                  <span className={`rnw-pill ${s.cls}`}>{s.label}</span>
                  {r.reminderStage > 0 && (
                    <span className="rnw-chip" title={`Chased ${r.reminderStage} time(s)`}>
                      chased ×{r.reminderStage}
                    </span>
                  )}
                </td>
                <td className="rnw-num">
                  <div className="rnw-actions-inner">
                    {r.isDeleted ? (
                      <Button variant="secondary" size="sm" onClick={() => restore(r)}>Restore</Button>
                    ) : (
                      <>
                        <Button variant="primary" size="sm" onClick={() => setRenewing(r)}>Renew</Button>
                        <Button variant="secondary" size="sm" onClick={() => setEditing(r)}>Edit</Button>
                        <button type="button" className="rnw-del" onClick={() => remove(r)} aria-label="Remove">✕</button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
          {list.length === 0 && (
            <tr><td colSpan={7}><p className="rnw-empty">
              {tab === 'board' ? 'Nothing needs attention. Everything is in date. 🎉' : 'No records match.'}
            </p></td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <OrgAdminLayout
      title="Renewals"
      subtitle="Licences, registrations and certificates that expire — and what's about to lapse"
      headerRight={<Button variant="primary" onClick={() => setEditing({})}>+ Add renewal</Button>}
    >
      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      {tab !== 'clients' && (
        <div className="rnw-toolbar">
          <input
            className="rnw-search" placeholder="🔎 Client, number or type…"
            value={search} onChange={(e) => setSearch(e.target.value)}
          />
          <Select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} aria-label="Status">
            <option value="all">Any status</option>
            {Object.entries(STATE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </Select>
          <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} aria-label="Type">
            <option value="all">Any type</option>
            {types.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
          </Select>
          {tab === 'all' && (
            <label className="rnw-check">
              <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
              Show removed
            </label>
          )}
          <span className="rnw-count">{rows.length} shown</span>
        </div>
      )}

      {/* ── Needs attention ── */}
      {tab === 'board' && (
        loading || !summary ? <Card><Skeleton.Text lines={7} /></Card> : (
          <div className="rnw-stack">
            <div className="rnw-tiles">
              <button type="button" className="rnw-tile rnw-tile-bad" onClick={() => setStateFilter('lapsed')}>
                <span className="rnw-tile-label">Lapsed</span>
                <span className="rnw-tile-value">{c.lapsed}</span>
                <span className="rnw-tile-hint">already expired</span>
              </button>
              <button type="button" className="rnw-tile rnw-tile-warn" onClick={() => setStateFilter('due_today')}>
                <span className="rnw-tile-label">Due today</span>
                <span className="rnw-tile-value">{c.dueToday}</span>
                <span className="rnw-tile-hint">last chance</span>
              </button>
              <button type="button" className="rnw-tile rnw-tile-warn" onClick={() => setStateFilter('due_week')}>
                <span className="rnw-tile-label">This week</span>
                <span className="rnw-tile-value">{c.dueWeek}</span>
                <span className="rnw-tile-hint">within 7 days</span>
              </button>
              <button type="button" className="rnw-tile" onClick={() => setStateFilter('due_soon')}>
                <span className="rnw-tile-label">Due soon</span>
                <span className="rnw-tile-value">{c.dueSoon}</span>
                <span className="rnw-tile-hint">inside its runway</span>
              </button>
              <div className="rnw-tile rnw-tile-quiet">
                <span className="rnw-tile-label">Tracked</span>
                <span className="rnw-tile-value">{c.total}</span>
                <span className="rnw-tile-hint">active records</span>
              </div>
            </div>

            <Card>
              <h3 className="rnw-h">Needs attention</h3>
              <p className="rnw-sub">
                Worst first. A reminder task is raised automatically as each one enters its runway,
                then again at a week out, on the day, and once more if it lapses.
              </p>
              {table(rows)}
            </Card>
          </div>
        )
      )}

      {/* ── All ── */}
      {tab === 'all' && (
        <Card>{loading || !all ? <Skeleton.Text lines={7} /> : table(rows)}</Card>
      )}

      {/* ── By client ── */}
      {tab === 'clients' && (
        <Card>
          <h3 className="rnw-h">Which clients are exposed</h3>
          <p className="rnw-sub">Who to call first — most lapsed licences at the top.</p>
          {loading || !summary ? <Skeleton.Text lines={6} /> : (
            <div className="rnw-scroll">
              <table className="rnw-table">
                <thead><tr><th>Client</th><th className="rnw-num">Lapsed</th><th className="rnw-num">Coming up</th></tr></thead>
                <tbody>
                  {summary.byClient.map((g) => (
                    <tr key={g.clientId} className={g.lapsed ? 'rnw-row-bad' : ''}>
                      <td><span className="rnw-name">{g.name}</span>{g.code && <span className="rnw-code">{g.code}</span>}</td>
                      <td className="rnw-num">{g.lapsed ? <b className="rnw-bad">{g.lapsed}</b> : '—'}</td>
                      <td className="rnw-num">{g.dueSoon || '—'}</td>
                    </tr>
                  ))}
                  {summary.byClient.length === 0 && (
                    <tr><td colSpan={3}><p className="rnw-empty">No client has anything outstanding.</p></td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {renewing && (
        <RenewalModal
          record={renewing}
          onClose={() => setRenewing(null)}
          onRenewed={() => { setRenewing(null); bump(); }}
        />
      )}
      {editing && (
        <RenewalEditModal
          record={editing}
          types={types}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); bump(); }}
        />
      )}
    </OrgAdminLayout>
  );
}

export default OrgAdminRenewals;
